import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  Compass,
  Sparkles,
  LineChart,
  Zap,
  ShieldCheck,
  Layers,
} from "lucide-react";

const disciplines = [
  {
    icon: <Compass size={22} />,
    t: "Brand Direction",
    d: "Positioning, naming, verbal identity, brand books, and long-view narrative — the language your brand will still be quoting five years from now.",
    deliverables: ["Positioning", "Naming", "Verbal Identity", "Brand Book"],
  },
  {
    icon: <Sparkles size={22} />,
    t: "Editorial Creative",
    d: "Campaigns shot and written to magazine-cover standard. Content people save, not scroll. Editorial-grade production, in-house.",
    deliverables: ["Campaigns", "Art Direction", "Photo & Film", "Copy"],
  },
  {
    icon: <LineChart size={22} />,
    t: "Performance",
    d: "Media planning, paid social, SEO and CRO — orchestrated against one honest growth number, reported without spin.",
    deliverables: ["Paid Media", "SEO", "CRO", "Analytics"],
  },
  {
    icon: <Zap size={22} />,
    t: "Community & PR",
    d: "Influencer, community, editorial press — the earned side of growth, done with taste. Where measurement and mystique meet.",
    deliverables: ["Influencer", "Community", "PR", "Events"],
  },
  {
    icon: <Layers size={22} />,
    t: "Digital Product",
    d: "Websites, storefronts and micro-tools that convert. Built by the same studio that made the brand — no handoffs, no dilution.",
    deliverables: ["Web", "Shopify", "Micro-tools", "Design System"],
  },
  {
    icon: <ShieldCheck size={22} />,
    t: "Growth Advisory",
    d: "A quarterly seat at the table for founders. Roadmapping, hiring, category strategy — the conversations that shape the next year.",
    deliverables: ["Roadmap", "Category", "Hiring", "Advisory"],
  },
];

export default function Services() {
  return (
    <div className="relative">
      <section className="pt-36 pb-16 grain relative overflow-hidden">
        <div className="max-w-[1240px] mx-auto px-6 sm:px-10 relative z-10">
          <p className="text-xs sm:text-sm uppercase tracking-[0.35em] text-[#1D2AE8]/80 font-semibold">
            Services · Six disciplines, one studio
          </p>
          <h1
            className="font-display leading-[0.92] mt-6"
            style={{ fontSize: "clamp(3rem, 10vw, 8.5rem)" }}
          >
            Everything a
            <br />
            modern brand
            <br />
            <em className="font-italic-serif text-[#FF3D6A]">actually</em>{" "}
            needs.
          </h1>
          <p className="mt-8 max-w-2xl text-lg text-[#1D2AE8]/85 leading-relaxed">
            We don't sell hours or headcount. We sell outcomes — and the six
            disciplines below are how we deliver them. Every engagement pulls
            from all six, weighted to what your brand needs this quarter.
          </p>
        </div>
      </section>

      <section className="py-16 pb-28">
        <div className="max-w-[1240px] mx-auto px-6 sm:px-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {disciplines.map((s, i) => (
            <motion.article
              key={s.t}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.55, delay: (i % 3) * 0.08 }}
              className="glass-light rounded-3xl p-7 h-full flex flex-col"
            >
              <div className="text-[#FF3D6A]">{s.icon}</div>
              <p className="font-display text-3xl mt-6">{s.t}</p>
              <p className="text-sm text-[#1D2AE8]/75 mt-3 leading-relaxed">
                {s.d}
              </p>
              <div className="mt-6 pt-5 border-t border-[#0B0B0B]/10 flex flex-wrap gap-2">
                {s.deliverables.map((d) => (
                  <span key={d} className="tag-pill">
                    {d}
                  </span>
                ))}
              </div>
            </motion.article>
          ))}
        </div>
      </section>

      <section className="py-24 bg-[#1D2AE8] text-[#FFF6E0]">
        <div className="max-w-[1100px] mx-auto px-6 sm:px-10 text-center">
          <p className="text-xs uppercase tracking-[0.3em] text-[#FFC629] font-semibold">
            Engagement models
          </p>
          <h2 className="font-display text-5xl sm:text-6xl leading-[0.95] mt-4">
            Three ways to
            <br />
            <em className="font-italic-serif text-[#FFC629]">work with us</em>.
          </h2>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-5 text-left">
            {[
              {
                t: "Retainer",
                d: "Quarterly engagements built around a signed growth number. Best for scaling brands.",
                cadence: "3 – 12 months",
              },
              {
                t: "Sprint",
                d: "6-week focused pushes — launches, rebrands, campaigns. Best for pivotal moments.",
                cadence: "6 weeks",
              },
              {
                t: "Advisory",
                d: "Monthly founder seat at the table. Roadmap, hiring, category strategy.",
                cadence: "12 months",
              },
            ].map((m) => (
              <div
                key={m.t}
                className="glass-ink rounded-3xl p-7"
              >
                <p className="text-xs uppercase tracking-[0.25em] text-[#FFC629]">
                  {m.cadence}
                </p>
                <p className="font-display text-3xl mt-3">{m.t}</p>
                <p className="text-sm text-[#FFF6E0]/70 mt-3 leading-relaxed">
                  {m.d}
                </p>
              </div>
            ))}
          </div>

          <Link
            to="/contact"
            className="mt-14 inline-flex items-center gap-2 bg-[#FFC629] text-[#0B0B0B] font-semibold rounded-full px-7 py-3.5 hover:bg-[#FF3D6A] hover:text-[#FFF6E0] transition"
          >
            Pick a model, send a brief <ArrowUpRight size={18} />
          </Link>
        </div>
      </section>
    </div>
  );
}
